import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import useQuery from 'hooks/apollo/useQuery';
import { RESIDUAL_VALUE_GRID_DETAILS, RESIDUAL_VALUE_GRID_ITEMS } from 'features/residualValueGrid/queries';
import { useReactiveVar } from '@apollo/client';
import { detailsLoadedItemIdsVar, highlightedItemIdVar } from '../../variables';
import FlexBox from 'components/common/FlexBox';
import { Card } from '@totalsoft/rocket-ui';
import ResidualValueGridItemsComponent from './ResidualValueGridItemsComponent';
import ResidualValueGridDetailsComponent from './ResidualValueGridDetailsComponent';
import { get, sequence, set } from '@totalsoft/change-tracking-react';
import { useTranslation } from 'react-i18next';

const itemActionsSlotId = 'combined-item-actions-slot';
const detailActionsSlotId = 'combined-detail-actions-slot';

const ResidualValueGridCombinedItemDetailsComponent = ({ id, lens, resetLens, validation, disabled, pending }) => {
  const { t } = useTranslation();

  const itemId = useReactiveVar(highlightedItemIdVar);
  const detailsLoadedItemIds = useReactiveVar(detailsLoadedItemIdsVar);

  const detailsLoaded = useMemo(() => detailsLoadedItemIds.includes(itemId), [detailsLoadedItemIds, itemId]);

  const { loading: loadingItems } = useQuery(RESIDUAL_VALUE_GRID_ITEMS, {
    variables: { id },
    skip: !id,
    onCompleted: data => {
      resetLens(prev => {
        return {
          items:
            data?.residualValueGridItems?.items?.map(x => ({
              id: x.itemId,
              name: x.itemName,
              details: prev.items.find(item => item.id === x.itemId)?.details || []
            })) || []
        };
      });
    }
  });

  const { loading: loadingDetails } = useQuery(RESIDUAL_VALUE_GRID_DETAILS, {
    variables: { gridId: id, itemId },
    skip: !id || !itemId || detailsLoaded,
    onCompleted: data => {
      const itemLens = (lens.items |> sequence).find(itemLens => (itemLens.id |> get) === itemId);
      (itemLens.details |> set)(data?.residualValueGridDetails?.details || []);
      detailsLoadedItemIdsVar(Array.from(new Set([...detailsLoadedItemIds, itemId])));
    }
  });

  const [itemLens, itemIndex] = (function () {
    let itemIndex;
    const itemLens = (lens.items |> sequence).find((itemLens, index) => {
      const found = (itemLens.id |> get) === itemId;
      if (found) itemIndex = index;
      return found;
    });
    return [itemLens, itemIndex];
  })();

  return (
    <FlexBox direction='column' gap={2}>
      <Card title={t('ResidualValueGrid.Items')} actions={<FlexBox gap={0.5} id={itemActionsSlotId} />}>
        <ResidualValueGridItemsComponent
          lens={lens.items}
          loading={loadingItems}
          actionsSlotId={itemActionsSlotId}
          disabled={disabled}
          pending={pending}
        />
      </Card>
      {itemId && itemLens && (
        <Card
          title={t('ResidualValueGrid.DepreciationGridFor', { itemName: itemLens.name |> get })}
          actions={<FlexBox gap={0.5} id={detailActionsSlotId} />}
        >
          <ResidualValueGridDetailsComponent
            lens={itemLens.details}
            loading={loadingDetails}
            actionsSlotId={detailActionsSlotId}
            validation={validation.items[itemIndex].details}
            disabled={disabled}
          />
        </Card>
      )}
    </FlexBox>
  );
};

ResidualValueGridCombinedItemDetailsComponent.propTypes = {
  id: PropTypes.number,
  lens: PropTypes.object,
  resetLens: PropTypes.func,
  validation: PropTypes.object,
  disabled: PropTypes.bool,
  pending: PropTypes.bool
};

export default ResidualValueGridCombinedItemDetailsComponent;
